import React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileRegisterForm from "@/components/auth/mobile/MobileRegisterForm";

/**
 * ==========================================
 * AFFILIATE PROGRAM - Registration Page
 * ==========================================
 * 
 * Trang đăng ký tài khoản, tích hợp xử lý mã giới thiệu (referral code).
 * 
 * Các chức năng chính:
 * - Kiểm tra mã giới thiệu từ URL hoặc cookie khi load trang
 * - Hiển thị trường nhập mã giới thiệu tùy chọn
 * - Xác thực mã giới thiệu thông qua Supabase
 * - Lưu mã giới thiệu tạm thời trước khi chuyển hướng (Google Auth)
 * - Tự động assign user vào affiliate system khi đăng ký thành công
 * 
 * Liên kết với bảng dữ liệu:
 * - referral_codes: Để xác thực mã giới thiệu
 * - referrals: Lưu thông tin giới thiệu khi đăng ký thành công
 * - referral_clicks: Ghi nhận lượt nhấp vào liên kết giới thiệu
 * - user_profiles: Lưu referred_by_affiliate
 * - affiliate_user_limits: Tạo default limits cho user mới
 * 
 * Luồng dữ liệu:
 * - getReferralCodeFromUrl/getStoredReferralCode -> form -> validateReferralCode -> userAssignmentService.assignUserToAffiliateOnRegistration
 * - Đối với Google Auth: lưu mã vào localStorage để xử lý trong AuthCallback.tsx
 */

// Desktop version components
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Mail, Lock, User, Phone, Gift, Eye, EyeOff, ChartBar, TrendingUp } from "lucide-react";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import '@/styles/phone-input-overrides.css';

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ImageWithFallback } from "@/components/common/ImageWithFallback";
import { useLogoPreloader } from "@/hooks/useLogoPreloader";
import { resolveReferralCode, validateReferralCode } from "@/utils/referralUtils";
import { TypingEffect } from "@/components/bot-builder/TypingEffect";
import RegistrationSuccessDialog from "@/components/auth/RegistrationSuccessDialog";
import { userAssignmentService } from "@/services/userAssignmentService";
import { GeolocationService } from "@/services/geolocationService";

// Flag to control Google authentication visibility - can be moved to environment variables later
const ENABLE_GOOGLE_AUTH = false;

// Update schema to include optional referral code
const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Invalid email address" }),
  phone: z.string().min(10, { message: "Phone number must be at least 10 digits" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
  referralCode: z.string().optional(),
  country: z.string().min(1, { message: "Country is required" }),
  countryCode: z.string().min(2, { message: "Country code is required" }),
});

const DesktopRegister = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [detectedReferralCode, setDetectedReferralCode] = React.useState<string | null>(null);
  const [isReferralReadOnly, setIsReferralReadOnly] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [animationStage, setAnimationStage] = React.useState(0);
  const [showSuccessDialog, setShowSuccessDialog] = React.useState(false);
  const [registeredEmail, setRegisteredEmail] = React.useState("");
  const [detectedCountry, setDetectedCountry] = React.useState<{ country: string; countryCode: string } | null>(null);
  
  // Preload logos as soon as component mounts
  useLogoPreloader();

  // Animation sequence control
  React.useEffect(() => {
    const sequence = [
      { stage: 1, delay: 500 },    // Show title
      { stage: 2, delay: 1000 },   // Start typing description
      { stage: 3, delay: 3500 },   // Show chart icon
      { stage: 4, delay: 4000 },   // Animate decorative elements
    ];

    let timer: ReturnType<typeof setTimeout>;
    let currentIndex = 0;

    const runNextAnimation = () => {
      if (currentIndex < sequence.length) {
        const { stage, delay } = sequence[currentIndex];
        setAnimationStage(stage);
        
        timer = setTimeout(() => {
          currentIndex++;
          runNextAnimation();
        }, delay);
      }
    };

    runNextAnimation();

    return () => clearTimeout(timer);
  }, []);

  // Check for referral codes in URL or cookies on page load
  React.useEffect(() => {
    const code = resolveReferralCode();
    if (code) {
      setDetectedReferralCode(code);
      setIsReferralReadOnly(true);
    }
  }, []);


  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      referralCode: "",
      country: "",
      countryCode: "",
    },
  });

  // Update the referral code field when detected from URL or cookie
  React.useEffect(() => {
    if (detectedReferralCode) {
      form.setValue('referralCode', detectedReferralCode);
    }
  }, [detectedReferralCode, form]);

  // Detect user's country on component mount
  React.useEffect(() => {
    const detectCountry = async () => {
      try {
        const countryData = await GeolocationService.detectCountry();
        setDetectedCountry(countryData);
        // Auto-set the country in the form
        form.setValue('country', countryData.country);
        form.setValue('countryCode', countryData.countryCode);
      } catch (error) {
        console.warn('Failed to detect country:', error);
        // Fallback to Vietnam
        setDetectedCountry({ country: 'Vietnam', countryCode: 'VN' });
        form.setValue('country', 'Vietnam');
        form.setValue('countryCode', 'VN');
      }
    };

    detectCountry();
  }, [form]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    
    try {
      console.log("🚀 [DESKTOP REGISTRATION] Starting simplified registration with immediate referral assignment");
      
      // Validate referral code if provided
      let referralData = null;
      if (values.referralCode) {
        referralData = await validateReferralCode(values.referralCode);
        if (values.referralCode && !referralData.isValid) {
          toast.error("Invalid referral code. Registration will continue without it.");
        }
      }
      
      // Register the user with Supabase
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            name: values.name,
            phone: values.phone,
            country: values.country,
            countryCode: values.countryCode,
            referralCode: referralData?.isValid ? values.referralCode : null,
            referredBy: referralData?.isValid ? referralData.affiliateId : null,
          },
        },
      });
      
      if (authError) {
        toast.error(authError.message);
        return;
      }
      
      // Save country data to user_profiles
      if (authData?.user) {
        try {
          const { error: profileError } = await supabase
            .from('user_profiles')
            .upsert({
              user_id: authData.user.id,
              country: values.country,
              country_code: values.countryCode,
              display_name: values.name,
              display_email: values.email,
            });

          if (profileError) {
            console.warn('Failed to save country data to profile:', profileError);
          }
        } catch (error) {
          console.warn('Error saving profile data:', error);
        }
      }

      // Process referral immediately if valid
      if (authData?.user && referralData?.isValid) {
        console.log("✅ [DESKTOP REGISTRATION] User registered successfully, starting affiliate assignment...");
        
        try {
          const assignmentSuccess = await userAssignmentService.assignUserToAffiliateOnRegistration(
            authData.user.id,
            referralData.affiliateId,
            values.referralCode
          );
          
          if (assignmentSuccess) {
            console.log("✅ [DESKTOP REGISTRATION] User successfully assigned to affiliate system during registration");
            toast.success("Registration successful! You've been assigned to your referrer's affiliate program.");
          } else {
            console.error("❌ [DESKTOP REGISTRATION] Failed to assign user to affiliate system during registration");
            toast.warning("Registration successful, but affiliate assignment failed. Please contact support.");
          }
        } catch (affiliateError) {
          console.error("❌ [DESKTOP REGISTRATION] Exception during affiliate assignment:", affiliateError);
          toast.warning("Registration successful, but affiliate assignment encountered an error.");
        }
      }
      
      // Store email and show success dialog
      setRegisteredEmail(values.email);
      setShowSuccessDialog(true);
      
    } catch (error) {
      console.error("❌ [DESKTOP REGISTRATION] Registration error:", error);
      toast.error("An unexpected error occurred during registration");
    } finally {
      setIsLoading(false);
    }
  }

  const handleGoogleSignUp = async () => {
    // Store the referral code in local storage before redirect
    const currentRefCode = form.getValues('referralCode') || detectedReferralCode;
    if (currentRefCode) {
      localStorage.setItem('pendingReferralCode', currentRefCode);
      console.log("💾 Stored referral code for OAuth processing:", currentRefCode);
    }
    
    setIsLoading(true);
    
    try {
      // Ensure we use the full origin for the redirect URL
      const redirectUrl = `${window.location.origin}/auth/callback`;
      console.log("Google signup redirect URL:", redirectUrl);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        }
      });
      
      if (error) {
        toast.error(error.message);
        console.error("Google OAuth error:", error);
      }
    } catch (error) {
      toast.error("Failed to connect with Google");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-botscheme-royal-purple/10 via-background to-botscheme-emerald-green/10">
      <div className="relative w-full max-w-5xl px-4 py-10">
        {/* Decorative elements */}
        <div className="absolute -z-10 h-56 w-56 -left-10 top-10 rounded-full bg-botscheme-royal-purple/20 blur-3xl"></div>
        <div className="absolute right-0 top-1/2 -z-10 h-72 w-72 rounded-full bg-botscheme-emerald-green/20 blur-3xl"></div>
        
        <div className="flex w-full flex-col-reverse overflow-hidden rounded-2xl bg-white/5 backdrop-blur-xl shadow-xl border border-white/10 lg:flex-row">
          {/* Form container */}
          <div className="w-full p-8 lg:w-1/2 lg:p-12">
            <div className="flex flex-col h-full justify-center">
              <div className="mb-6 flex justify-center lg:justify-start">
                <div className="w-64 h-24 relative">
                  <ImageWithFallback 
                    src="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20trade%20bot%20365.png" 
                    alt="Trade Bot 365 Logo" 
                    className="w-full h-full object-contain"
                    loading="eager"
                    skeletonClassName="bg-muted/50"
                  />
                </div>
              </div>
              
              <div className="space-y-4 animate-fade-in">
                <div className="text-center lg:text-left">
                  <h1 className="text-2xl font-bold lg:text-3xl">Create an account</h1>
                  <p className="mt-2 text-muted-foreground">Enter your information to join Trade Bot 365</p>
                  {detectedReferralCode && (
                    <div className="mt-3">
                      <div className="inline-flex items-center px-3 py-1 rounded-full bg-botscheme-coral-orange/20 text-botscheme-coral-orange text-sm font-medium animate-pulse">
                        <Gift className="h-4 w-4 mr-1" /> Referral code applied: {detectedReferralCode}
                      </div>
                    </div>
                  )}
                </div>
                
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Name</FormLabel>
                          <div className="relative">
                            <User className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                            <FormControl>
                              <Input 
                                placeholder="John Doe" 
                                className="pl-10 bg-white/5 border-white/10 focus:border-botscheme-ocean-blue transition-all" 
                                {...field} 
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <div className="relative">
                              <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                              <FormControl>
                                <Input 
                                  placeholder="you@example.com" 
                                  className="pl-10 bg-white/5 border-white/10 focus:border-botscheme-ocean-blue transition-all" 
                                  {...field} 
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Phone Number
                              {detectedCountry && (
                                <span className="ml-2 text-xs bg-botscheme-emerald-green/20 text-botscheme-emerald-green px-2 py-0.5 rounded-full">
                                  {detectedCountry.country}
                                </span>
                              )}
                            </FormLabel>
                            <FormControl>
                              <PhoneInput
                                defaultCountry={detectedCountry?.countryCode?.toLowerCase() || 'vn'}
                                value={field.value}
                                onChange={(phone, meta) => {
                                  field.onChange(phone);
                                  // Update country when user selects different country
                                  if (meta.country) {
                                    form.setValue('countryCode', meta.country.iso2.toUpperCase());
                                    form.setValue('country', meta.country.name);
                                  }
                                }}
                                inputClassName="bg-white/5 border-white/10 focus:border-botscheme-ocean-blue transition-all"
                                countrySelectorStyleProps={{
                                  buttonClassName: "bg-white/5 border-white/10 hover:bg-white/10 transition-all",
                                  dropdownStyleProps: {
                                    className: "bg-background border-white/10"
                                  }
                                }}
                                placeholder="Enter phone number"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <div className="relative">
                            <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                            <FormControl>
                              <Input 
                                type={showPassword ? "text" : "password"}
                                placeholder="••••••••" 
                                className="pl-10 bg-white/5 border-white/10 focus:border-botscheme-ocean-blue transition-all" 
                                {...field} 
                              />
                            </FormControl>
                            <button 
                              type="button" 
                              className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="referralCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Referral Code {isReferralReadOnly && (
                              <span className="ml-2 text-xs bg-botscheme-coral-orange/20 text-botscheme-coral-orange px-2 py-0.5 rounded-full">
                                Applied
                              </span>
                            )}
                          </FormLabel>
                          <div className="relative">
                            <Gift className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                            <FormControl>
                              <Input 
                                placeholder={isReferralReadOnly ? "" : "Enter referral code"} 
                                readOnly={isReferralReadOnly}
                                className={`pl-10 bg-white/5 border-white/10 focus:border-botscheme-ocean-blue transition-all ${
                                  isReferralReadOnly ? "bg-white/10" : ""
                                }`}
                                {...field} 
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-botscheme-ocean-blue to-botscheme-emerald-green hover:opacity-90 transition-all shadow-lg" 
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating account...
                        </span>
                      ) : "Create Account"}
                    </Button>
                  </form>
                </Form>
                
                {/* Google OAuth Section - Hidden based on flag */}
                {ENABLE_GOOGLE_AUTH && (
                  <>
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/10"></div>
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground rounded">Or continue with</span>
                      </div>
                    </div>
                    
                    <Button 
                      variant="outline" 
                      className="w-full border-white/10 bg-white/5 hover:bg-white/10 transition-all backdrop-blur-sm" 
                      onClick={handleGoogleSignUp}
                      disabled={isLoading}
                    >
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      Sign up with Google
                    </Button>
                  </>
                )}
                
                <div className="text-center mt-6">
                  <Button 
                    variant="link" 
                    className="text-botscheme-ocean-blue hover:text-botscheme-royal-purple transition-colors"
                    onClick={() => navigate("/auth/login")}
                  >
                    Already have an account? Sign in
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side - Info Panel with gradient background */}
          <div className="w-full h-64 lg:h-auto lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-botscheme-royal-purple to-botscheme-ocean-blue p-8 lg:p-12 flex items-center justify-center">
            {/* Decorative floating elements */}
            <div className="absolute top-10 right-10 w-20 h-20 rounded-full bg-white/10 blur-xl animate-pulse"></div>
            <div className="absolute bottom-20 left-10 w-32 h-32 rounded-full bg-white/5 blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 rounded-full bg-botscheme-emerald-green/20 blur-lg animate-pulse" style={{animationDelay: '2s'}}></div>
            
            <div className="text-center space-y-6 relative z-10">
              {animationStage >= 1 && (
                <div className="animate-fade-in">
                  <h2 className="text-2xl font-bold text-white/90 lg:text-3xl">
                    Welcome to Trade Bot 365
                  </h2>
                </div>
              )}
              
              {animationStage >= 2 && (
                <div className="animate-fade-in-delayed">
                  <TypingEffect 
                    text="Join thousands of traders who trust our AI-powered trading platform. Experience the future of automated trading with advanced algorithms and real-time market analysis."
                    className="text-white/70 text-lg leading-relaxed"
                    speed={30}
                  />
                </div>
              )}
              
              {animationStage >= 3 && (
                <div className="animate-bounce-in">
                  <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r from-botscheme-emerald-green to-botscheme-ocean-blue">
                    <ChartBar className="w-12 h-12 text-white" />
                  </div>
                </div>
              )}
              
              {animationStage >= 4 && (
                <div className="animate-fade-in space-y-4">
                  <div className="flex items-center justify-center space-x-8">
                    <div className="text-center">
                      <TrendingUp className="w-8 h-8 text-white/80 mx-auto mb-2" />
                      <p className="text-white/60 text-sm">AI Trading</p>
                    </div>
                    <div className="text-center">
                      <ChartBar className="w-8 h-8 text-white/80 mx-auto mb-2" />
                      <p className="text-white/60 text-sm">Analytics</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Success Dialog */}
      <RegistrationSuccessDialog 
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        email={registeredEmail}
      />
      
      {/* Add floating animation keyframes */}
      <style>
        {`
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        `}
      </style>
    </div>
  );
};

const Register = () => {
  const isMobile = useIsMobile();
  
  // Render mobile or desktop version based on device detection
  return isMobile ? <MobileRegisterForm /> : <DesktopRegister />;
};

export default Register;
